import { createNpiSteeringDeletionRequestNotification } from './createNpiSteeringDeletionRequestNotification';
import { fetchIndusRecords, getIndusIdByName } from './indusService';
import { resolveSessionFeatureAccess } from './sessionFeatureAccess';

export type ProcessDeletedRowsResult =
  | { success: true; sheet1DeleteIds: Array<number | string>; errorMessage?: undefined }
  | {
      success: false;
      errorMessage: string;
      sheet1DeleteIds: [];
    };

export type ProcessDeletedRowsParams = {
  deletedRow: any[];
  roleAccessMap: Record<string, boolean>;
  screenGridId: number;
  userId: number;
  sheet1UpdateObjects: Array<Record<string, unknown>>;
  resolveSheet1DeleteIds: (rows: any[]) => Array<number | string>;
};

/**
 * Delete handling strategy by role and delete feature access.
 *
 * Priority:
 * - `Administrator`: direct delete.
 * - non-admin with `delete_row` feature access: notification workflow.
 * - no `delete_row` access: delete not allowed.
 */
type DeleteHandlingStrategy = 'direct_delete' | 'notification_workflow' | 'not_allowed';

async function resolveDeleteHandlingStrategy(
  screenGridId: number,
  roleAccessMap: Record<string, boolean>,
): Promise<DeleteHandlingStrategy | null> {
  if (roleAccessMap['Administrator']) {
    return 'direct_delete';
  }

  const hasDeleteFeatureAccess = await resolveSessionFeatureAccess(screenGridId);

  if (hasDeleteFeatureAccess === null) {
    return null;
  }

  if (hasDeleteFeatureAccess) {
    return 'notification_workflow';
  }

  return 'not_allowed';
}

const isDeletionRequestedIndus = (indus: unknown) =>
  typeof indus === 'string' && indus.trim().toLowerCase() === 'deletion requested';

/**
 * Processes delete-intent rows for NPI Steering commit.
 *
 * Business logic:
 * - No rows to delete: success with no delete ids.
 * - `Administrator`: perform normal delete path and return `sheet1DeleteIds`.
 * - non-admin with `delete_row`: convert delete operation into
 *   "Deletion Requested" update and create a deletion-request notification for each row.
 * - no `delete_row` access: fail with unauthorized delete error.
 *
 * @returns A discriminated result containing success flag, delete ids, and optional error.
 */
export async function processDeletedRows({
  deletedRow,
  screenGridId,
  userId,
  sheet1UpdateObjects,
  resolveSheet1DeleteIds,
  roleAccessMap,
}: ProcessDeletedRowsParams): Promise<ProcessDeletedRowsResult> {
  if (!deletedRow.length) {
    return { success: true, sheet1DeleteIds: [] };
  }

  const deleteHandlingStrategy = await resolveDeleteHandlingStrategy(screenGridId, roleAccessMap);

  if (!deleteHandlingStrategy) {
    return {
      success: false,
      errorMessage: 'Unable to validate delete feature access from active session',
      sheet1DeleteIds: [],
    };
  }

  if (deleteHandlingStrategy === 'direct_delete') {
    return {
      success: true,
      sheet1DeleteIds: resolveSheet1DeleteIds(deletedRow),
    };
  }

  if (deleteHandlingStrategy === 'not_allowed') {
    return {
      success: false,
      errorMessage: 'delete_not_allowed_for_role',
      sheet1DeleteIds: [],
    };
  }

  const hasAlreadyRequestedDeletionRows = deletedRow.some((row) =>
    isDeletionRequestedIndus(row?.indus),
  );

  if (hasAlreadyRequestedDeletionRows) {
    return {
      success: false,
      errorMessage: 'deletion_already_requested_pending',
      sheet1DeleteIds: [],
    };
  }

  const indusRecords = await fetchIndusRecords(screenGridId);
  const deletionRequestedIndusId = getIndusIdByName(indusRecords, 'Deletion Requested');

  if (deletionRequestedIndusId == null) {
    return {
      success: false,
      errorMessage: 'Deletion Requested INDUS value not found',
      sheet1DeleteIds: [],
    };
  }

  const deleteRequestUpdateObjects = deletedRow.map((row) => ({
    where: {
      id1: {
        _eq: row.id1,
      },
    },
    _set: {
      indus_id: deletionRequestedIndusId,
      updated_by: userId,
      update_date: new Date().toISOString().substring(0, 19),
    },
  }));

  sheet1UpdateObjects.push(...deleteRequestUpdateObjects);

  await Promise.all(
    deletedRow.map((row) =>
      createNpiSteeringDeletionRequestNotification({
        row: row as any,
        previousIndusId: getIndusIdByName(indusRecords, row?.indus) ?? null,
        screenGridId,
        createdBy: userId,
      }),
    ),
  );

  return { success: true, sheet1DeleteIds: [] };
}
